import React, { useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { getAPI } from 'store/apiReducer';
import ChampsAutocomplete from '../../../../../../components/form/ChampAutocomplete/ChampsAutocomplete';
import usagerClientReducer from '../../../../../../store/usagerReducer';
import {
    API_GET_METIERS_ET_ASSOCIATIONS_TYPES_URL,
    CONTEXT_GET_METIERS_ET_ASSOCIATIONS_TYPES,
} from '../../../../../../constants/apiUrls.constants';
import {
    KEY_DETENTEUR_ASSO_MORALE,
    KEY_DETENTEUR_METIER_MORALE,
    KEY_DETENTEUR_METIER_PHYSIQUE,
} from './StatutCompte.constante';

const SelectMetierAssociation = ({
    metiersEtAssociationsTypes,
    getApi,
    isLoading,
    name,
    label,
    texteAide,
    placeholder,
    error,
    disabled,
    required,
    statut,
}) => {
    const { setValue, getValues, trigger } = useFormContext();
    const [valueRecherche, setValueRecherche] = useState('');
    const [showAllOptions, setShowAllOptions] = useState(true);
    const [prevStatut, setPrevStatut] = useState(null);

    const getMetiersEtAssociationsTypes = () => {
        getApi(
            usagerClientReducer,
            API_GET_METIERS_ET_ASSOCIATIONS_TYPES_URL,
            CONTEXT_GET_METIERS_ET_ASSOCIATIONS_TYPES,
        );
    };

    useEffect(() => {
        if (
            isLoading === null &&
            (!metiersEtAssociationsTypes || Object.keys(metiersEtAssociationsTypes).length === 0)
        ) {
            getMetiersEtAssociationsTypes();
        }
    }, [metiersEtAssociationsTypes, getMetiersEtAssociationsTypes]);

    useEffect(() => {
        if (prevStatut && prevStatut !== statut) {
            setValue(name, '');
            trigger(name).then(() => {});
        }
        setPrevStatut(statut);
    }, [statut, prevStatut, name]);

    const filteredOptions = useMemo(() => {
        if (!metiersEtAssociationsTypes || !statut) {
            return [];
        }

        const getOptionsForStatut = () => {
            const { associations, metiers } = metiersEtAssociationsTypes;

            switch (statut) {
                case KEY_DETENTEUR_ASSO_MORALE:
                    return associations?.personne_morale || {};
                case KEY_DETENTEUR_METIER_MORALE:
                    return metiers?.personne_morale || {};
                case KEY_DETENTEUR_METIER_PHYSIQUE:
                    return metiers?.personne_physique || {};
                default:
                    return {};
            }
        };

        const sourceOptions = getOptionsForStatut();

        return Object.entries(sourceOptions).map(([key, value]) => ({
            key,
            value: key,
            libelle: value,
        }));
    }, [metiersEtAssociationsTypes, statut]);

    const getSuggestionValue = (suggestion) => {
        return suggestion.libelle;
    };

    const handleChange = (event, recherche) => {
        if (recherche.newValue === '') {
            setValue(name, '');
            trigger(name).then(() => {});
        }
        setValueRecherche(recherche.newValue);
        setShowAllOptions(false);
    };

    const handleSuggestionSelected = (event, suggestion) => {
        setValue(name, suggestion.suggestion.key);
        trigger(name).then(() => {});
        setShowAllOptions(false);
    };

    const handleSuggestionsFetchRequested = () => {};

    const renderSuggestion = (suggestion) => {
        return <span>{suggestion.libelle}</span>;
    };

    const suggestionsMetiersEtAssociations = useMemo(() => {
        if (showAllOptions) {
            return filteredOptions;
        }

        if (!valueRecherche) return filteredOptions;

        const searchTerm = valueRecherche.toLowerCase();
        return filteredOptions.filter((option) =>
            option.libelle.toLowerCase().includes(searchTerm),
        );
    }, [valueRecherche, filteredOptions, showAllOptions]);

    const handleFocus = () => {
        setShowAllOptions(true);
    };

    return (
        <Controller
            render={({ fieldState }) => (
                <ChampsAutocomplete
                    label={label}
                    suggestions={suggestionsMetiersEtAssociations}
                    getSuggestionValue={getSuggestionValue}
                    inputProps={{
                        name,
                        onChange: handleChange,
                        onFocus: handleFocus,
                        value: getValues(name)
                            ? filteredOptions.find((opt) => opt.key === getValues(name))?.libelle ||
                              ''
                            : valueRecherche,
                        placeholder: metiersEtAssociationsTypes
                            ? placeholder
                            : 'Chargement en cours...',
                        disabled: disabled || !statut,
                        required,
                    }}
                    onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                    onSuggestionSelected={handleSuggestionSelected}
                    renderSuggestion={renderSuggestion}
                    afficherRequired={required}
                    error={fieldState.error || error}
                    description={texteAide}
                    shouldRenderOnFocus
                />
            )}
            name={name}
            rules={{
                required: required ? `Le champ ${label.toLowerCase()} est obligatoire` : false,
            }}
        />
    );
};

SelectMetierAssociation.propTypes = {
    name: PropTypes.string.isRequired,
    metiersEtAssociationsTypes: PropTypes.shape({
        associations: PropTypes.shape({
            personne_morale: PropTypes.object,
        }),
        metiers: PropTypes.shape({
            personne_morale: PropTypes.object,
            personne_physique: PropTypes.object,
        }),
    }),
    getApi: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    label: PropTypes.string,
    texteAide: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    statut: PropTypes.string,
    error: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    warning: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
    valid: PropTypes.shape({
        message: PropTypes.string.isRequired,
    }),
};

SelectMetierAssociation.defaultProps = {
    label: 'Type de métier ou association',
    disabled: false,
    required: false,
    texteAide: null,
    placeholder: '-- Choisir un type --',
    isLoading: null,
    metiersEtAssociationsTypes: null,
    statut: null,
};

export default connect(
    (state) => {
        const { isLoading, erreur, data, message } = state.referentiel.metiersEtAssociationsTypes;
        return {
            isLoading,
            erreur,
            metiersEtAssociationsTypes: data,
            message,
        };
    },
    {
        getApi: getAPI,
    },
)(SelectMetierAssociation);
